import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { MappedProduct, VariantDetails } from '../../integrations/relewise/types/mapped';
import { getCartState } from '@msdyn365-commerce/global-state';
import { useState } from 'react';
import { CART_LINE_QUANTITY_INCREASED_EVENT } from '../const/events';
import { ProductType } from '@msdyn365-commerce/retail-proxy';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';

interface UseAddToCartArgs {
    context: ICoreContext<{
        [x: string]: any;
    }>;
    selectedVariant: VariantDetails | MappedProduct | undefined;
    productItemId: string | undefined;
    productRecordId: number | undefined;
    unitOfMeasure: string | undefined;
    telemetry: ITelemetry;
    moduleName: string;
}

export const useAddToCart = ({
    context,
    selectedVariant,
    productItemId,
    productRecordId,
    unitOfMeasure,
    telemetry,
    moduleName
}: UseAddToCartArgs) => {
    const [addToCartLoading, setAddToCartLoading] = useState(false);
    const [hasError, setHasError] = useState<boolean>(false);

    const handleAddToCartClick = async (variantOverride?: VariantDetails | MappedProduct): Promise<{ success: boolean }> => {
        const variant = variantOverride ?? selectedVariant;
        const { variantRecordId, basePrice, finalPrice } = variant ?? {};
        const catalogId = context.request.apiSettings.catalogId;

        try {
            if (!variantRecordId) {
                throw new Error('Missing variantRecordId');
            }
            if (!basePrice || !finalPrice) {
                throw new Error('Missing variant price data');
            }
            if (!unitOfMeasure) {
                throw new Error('Missing unitOfMeasure');
            }
            if (catalogId === undefined) {
                throw new Error('Missing catalogId');
            }

            setAddToCartLoading(true);
            setHasError(false);

            const cartState = await getCartState(context.actionContext);
            const maxQuantity: number | undefined = context.app.config.maxQuantityForCartLineItem;
            const cartLines = cartState.cart.CartLines;
            const existingLine = cartLines?.find(line => {
                const lineQuantity = line.Quantity ?? 0;
                const hasReachedMaxQuantity = maxQuantity !== undefined && lineQuantity >= maxQuantity;
                return line.ProductId === variantRecordId && !hasReachedMaxQuantity;
            });

            const addToCartResult = existingLine?.LineId
                ? await cartState.updateCartLineQuantity({
                      cartLineId: existingLine.LineId,
                      newQuantity: (existingLine?.Quantity ?? 0) + 1
                  })
                : await cartState.addProductToCart({
                      product: {
                          RecordId: variantRecordId,
                          ItemId: productItemId,
                          MasterProductId: productRecordId,
                          Name: variant?.displayName,
                          BasePrice: basePrice,
                          AdjustedPrice: finalPrice,
                          Price: basePrice,
                          ProductTypeValue: ProductType.Variant,
                          DefaultUnitOfMeasure: unitOfMeasure
                      },
                      count: 1,
                      catalogId,
                      enableStockCheck: context.app.config.enableStockCheck
                  });
            if (addToCartResult.status === 'FAILED') {
                throw new Error(addToCartResult.errorDetails?.LocalizedMessage || 'Add to cart request failed');
            }
            if (existingLine?.LineId) {
                window.dispatchEvent(new Event(CART_LINE_QUANTITY_INCREASED_EVENT));
            }
        } catch (error) {
            setHasError(true);
            const message = `[${moduleName}] Error adding to cart: ${error instanceof Error ? error.message : String(error)}`;
            console.error(message);
            telemetry.error(message);

            setAddToCartLoading(false);
            return { success: false };
        }

        setAddToCartLoading(false);
        return { success: true };
    };

    const clearError = () => setHasError(false);

    return { handleAddToCartClick, addToCartLoading, hasError, clearError };
};
